import { playDetail } from 'app/components/action-center/playDetail'
import {
  type ActionPlay,
  type ActionQueueDef,
  actionPlayFor,
  hygieneDaysUntilDue,
  hygieneDueOn,
} from 'app/components/action-center/queueRegistry'
import type { BoardTask } from 'app/components/action-center/useBoardTasks'
import {
  type BoardCardFacts,
  type UrgencyTone,
  ageFrom,
  boardCardFacts,
  cardTags,
  isInConversation,
  leadUrgencyTone,
  urgencyTone,
} from 'app/lib/boardCardFacts'
import { type TaskBadge, mostUrgentTaskBadge } from 'app/lib/taskBadges'
import type { ContactJson } from 'app/types/entities'

/** Everything a system board card works out before it draws. Named as `BoardCard` named them. */
export type ContactCardFacts = {
  play: ActionPlay
  facts: BoardCardFacts
  age: number | undefined
  tone: UrgencyTone
  inConversation: boolean
  taskBadge: TaskBadge | undefined
  lead: boolean
  tags: { shown: string[]; extra: number }
  showPlay: boolean
  recall: boolean
  appointmentWorthSaying: boolean
  prose: string | undefined
  figure: string | undefined
}

/** Computed once per card, so every section reads the same answer and stays thin. */
export function contactCardFacts(def: ActionQueueDef, contact: ContactJson, tasks?: BoardTask[]): ContactCardFacts {
  const play = actionPlayFor(def.id, contact)
  const detail = playDetail(play, contact)
  const facts = boardCardFacts(contact, def.family)
  const age = ageFrom(contact.birthDate)
  const hygiene = play.timeline === `hygiene`
  // The play's own tone is identical on every card in a queue, so urgency
  // grades off what actually varies: lateness on hygiene, wait on leads.
  const tone: UrgencyTone = hygiene ? urgencyTone(hygieneDaysUntilDue(contact)) : leadUrgencyTone(contact)
  const inConversation = isInConversation(contact)
  const taskBadge = mostUrgentTaskBadge(tasks ?? [])
  const lead = def.family === `lead`
  // No appointment chip on a lead card, so its appointment tags stay (see `cardTags`).
  const tags = cardTags(contact.tags, { appointmentShown: !lead })
  // The play gives way where the tags or the appointment chip already say the
  // same thing in another vocabulary.
  const appointmentSaysIt = /appointment/i.test(play.label)
  const showPlay = !hygiene && !lead && tags.shown.length === 0 && !appointmentSaysIt
  const recall = hygiene && !!hygieneDueOn(contact)
  // `gray` means "Nothing booked", which is true of every card in these queues,
  // so only a booking or a broken one is worth a chip.
  const appointmentWorthSaying = !lead && facts.appointment.tone !== `gray`
  const prose = hygiene || lead ? undefined : detail
  const figure = hygiene ? detail : prose ? undefined : facts.age
  return {
    play,
    facts,
    age,
    tone,
    inConversation,
    taskBadge,
    lead,
    tags,
    showPlay,
    recall,
    appointmentWorthSaying,
    prose,
    figure,
  }
}
